import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { useEffect, useState } from 'react';

import { Account } from './account';
import { AccountEventListener } from './account-event-listener';
import { AccountClient } from './api/account-client';
import { seedAccounts } from './redux/account-slice';

declare global {
    interface WindowEventMap {
        'useAccount:request': CustomEvent<string>;
    }
}

const REQUEST_ACCOUNT_EVENT = 'useAccount:request';
const STORAGE_KEY = 'ACS_REQ';

export function accountRequest(accountId: string) {
    window.dispatchEvent(new CustomEvent(REQUEST_ACCOUNT_EVENT, { detail: accountId }));
}

interface Props {
    dispatch: ThunkDispatch<any, any, AnyAction>;
    enablePubSub?: boolean;
}

export function useAccounts(props: Props) {
    const [pending, changePending] = useState<string[]>([]);
    const [timeoutSet, changeTimeoutSet] = useState<boolean>(false);
    const [timeoutFinished, changeTimeoutFinished] = useState<boolean>(false);
    const [accountEventListener, changeAccountEventListener] = useState<AccountEventListener | null>(null);

    useEffect(() => {
        window.addEventListener(REQUEST_ACCOUNT_EVENT, listener);
        if (props.enablePubSub) changeAccountEventListener(AccountEventListener.getInstance(props.dispatch));
    }, []);

    useEffect(() => {
        if (timeoutFinished) {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored != null) {
                changePending(JSON.parse(stored));
                changeTimeoutSet(false);
                changeTimeoutFinished(false);
                localStorage.removeItem(STORAGE_KEY);
            }
        }
    }, [timeoutFinished]);

    useEffect(() => {
        if (pending.length > 0) {
            getAccounts(pending).then((result) => {
                props.dispatch(seedAccounts(result));
                changePending([]);
                changeTimeoutSet(false);
                changeTimeoutFinished(false);
            });
        }
    }, [pending]);

    const listener = (event: CustomEvent) => {
        if (typeof event.detail == 'string') {
            if (timeoutSet === false) {
                changeTimeoutSet(true);
                setTimeout(() => {
                    changeTimeoutFinished(true);
                }, 1000);
            }
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored == null) {
                localStorage.setItem(STORAGE_KEY, JSON.stringify([event.detail]));
            } else if (!JSON.parse(stored).includes(event.detail)) {
                localStorage.setItem(STORAGE_KEY, JSON.stringify([...JSON.parse(stored), event.detail]));
            }
        }
    };

    return { accountEventListener };
}

async function getAccounts(accountIds: string[]): Promise<Account[]> {
    const response = await AccountClient.resolveAccounts(accountIds);
    return response.results;
}
